/** @format */

import React, { useEffect, useState, FunctionComponent } from 'react'
import TablePage from 'src/components/TablePage'
import {
  _$,
  Tooltip,
  Icon,
  Message,
  Button,
  Tag,
  Switch,
  Editable,
  Dialog
} from '@king-design/react'
import useFormTable from 'src/hooks/useFormTable'
import { useHistory } from 'react-router'
import { API, EditEnablePolicy, DeleteAlarmPolicy } from 'src/api/ApiAlarmServer'
import { MonitorSearch, Divider, CollapsibleList } from 'src/components'
import styled from 'styled-components'
import variable from 'src/static/styles/var'
import useTableGroup from 'src/hooks/useTableGroup'
import { handleMetricUnits } from 'src/containers/MonitorCreate/AlarmRule/constants'
import { methodObject, compareObject } from 'src/config'
import { request } from 'src/utils'
import TacticsInstancesDialog from './TacticsInstancesDialog'
import { getTemplateList } from 'src/api/ApiAlarmTemplate'
import { useAsyncFn } from 'react-use'

interface OwnProps {
  className?: string
}

export const renderTriggerRule = (productType, rule) => {
  const {
    itemKey,
    itemName,
    triggerValue,
    units,
    displayUnits,
    period,
    method,
    compare,
    effectBT,
    effectET,
    ruleId
  } = rule
  if (itemKey === 'agent.ping' || itemKey === 'kdc.alive') {
    return itemName
  }
  const value = handleMetricUnits(productType, itemKey, triggerValue, units, displayUnits)
  const text = `${itemName} ${parseInt(period)}${_$('分钟')} ${methodObject[method]} ${
    compareObject[compare] || compare
  } ${value}${displayUnits || units} ${_$('告警生效时间')}${effectBT}-${effectET}`
  return (
    <div key={ruleId}>
      <Tooltip key={ruleId} content={_$(text)}>
        <span>{_$(text)}</span>
      </Tooltip>
    </div>
  )
}
const TacticsList: FunctionComponent<OwnProps> = ({ className }) => {
  const history = useHistory()
  const [tableGroup, setTableGroup] = useState({
    productName: 999
  })
  const productList = useTableGroup('productList')
  const [policyList, setPolicyList] = useState([])
  const [policyName, setPolicyName] = useState<string>('')
  // 已选实例dialog
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const [dialogPolicId, setDialogPolicId] = useState<number>()
  const [dialogProjectName, setDialogProjectName] = useState<string>('')

  const [{ value: templateList }, template_fetch] = useAsyncFn(async () => {
    const result = await getTemplateList({
      PageIndex: 1,
      PageSize: 9999,
      ProductType: 999
    })
    return result
  }, [])

  // 告警策略名称验证
  const policyNameValidate = value => {
    return /^[a-z,\u4E00-\u9FA5,0-9,A-Z,-_@#.]{2,64}$/.test(value)
  }

  // 初始化
  useEffect(() => {
    template_fetch()
  }, [])

  // 修改告警策略名称
  const changePolictName = async (name, id) => {
    const result = await request(API.ModifyPolicyName, {
      method: 'PUT',
      data: {
        policyId: id,
        policyName: name
      }
    })
    if (result?.code === 'Success') {
      Message.success(_$('修改成功'))
      run()
    }
  }

  // 设置/取消默认告警策略
  const handleDefaultPolicy = async (id, type) => {
    const result = await request(type === 1 ? API.CancelPolicyDefault : API.SetPolicyAsDefault, {
      method: 'POST',
      data: {
        policyId: id
      }
    })
    if (result?.code === 'Success') {
      Message.success(_$('修改成功'))
      run()
    }
  }

  const getTemplateName = (id: number) => {
    if (templateList?.length && id) {
      return templateList?.find(item => item.TemplateID === id)?.TemplateName || ''
    }
    return ''
  }

  const schemeThreshold = {
    policyName: {
      width: '10%',
      title: _$('策略名称'),
      template: function (item) {
        return (
          <div className="policyType_cell">
            <Editable
              value={item.policyName}
              validate={value => policyNameValidate(value)}
              onChangeValue={value => {
                changePolictName(value, item.policyId)
              }}
            >
              <p className="metric-legend">{item.policyName}</p>
            </Editable>
          </div>
        )
      }
    },
    productName: {
      title: _$('产品类型'),
      width: '10%',
      group: productList,
      template: function (item) {
        return (
          <>
            {item.productName}
            {item.policyType === 1 ? (
              <Tag type="primary" size="mini">
                {_$('默认')}
              </Tag>
            ) : null}
          </>
        )
      }
    },
    triggerRules: {
      title: _$('告警规则'),
      width: '30%',
      template: function (item) {
        const { productType, templateId, triggerRules } = item
        const templateName = getTemplateName(templateId)
        return (
          <div className="rule-list">
            {templateId ? (
              <h4 className="text-overflow" style={{ fontWeight: 700 }}>
                <Tooltip content={templateName}>
                  <span>
                    {_$('关联模板名称')}：{templateName}
                  </span>
                </Tooltip>
              </h4>
            ) : null}
            <CollapsibleList
              data={triggerRules}
              renderItem={item => renderTriggerRule(productType, item)}
            />
          </div>
        )
      }
    },
    instanceIds: {
      title: _$('关联实例'),
      width: '10%',
      template: function (item) {
        return (
          <>
            {item?.resourceBindType === 2 ? (
              <div
                className="cursor-pointer"
                onClick={() => openDialog(item?.policyId, item?.projectName || '')}
              >
                <p>
                  <a>
                    {_$('项目')}：{item.projectName || ''}
                  </a>
                </p>
                <p>
                  <a>
                    {_$('实例')}：
                    {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
                  </a>
                </p>
              </div>
            ) : null}
            {item?.resourceBindType === 127 ? (
              <p>
                {_$('项目')}：{item.projectName || ''}
                <span className="deleted_color">({_$('已删除')})</span>
              </p>
            ) : null}
            {item?.resourceBindType !== 2 && item?.resourceBindType !== 127 ? (
              <a onClick={() => openDialog(item?.policyId, '')}>
                {_$('实例')}：
                {item.instanceIds ? item.instanceIds.length : item.instanceInfoList.length}
              </a>
            ) : null}
          </>
        )
      }
    },
    contactInfoList: {
      width: '10%',
      title: (
        <span>
          {_$('告警联系人')}
          <Tooltip content={_$('未配置告警联系人将不会收到告警')}>
            <Icon
              style={{ fontSize: '14px', marginLeft: '4px' }}
              className="icon ion-help-circled"
            />
          </Tooltip>
        </span>
      ),
      template: function (item) {
        const contactList = item.userNotice
        const group = contactList && [].concat(contactList).filter(i => i?.contactFlag === 1)
        const user = contactList && [].concat(contactList).filter(i => i?.contactFlag === 2)
        return (
          <span>
            {!contactList || !contactList?.length ? (
              <span
                className="none-setting"
                onClick={() => history.push(`/monitor/alarmServer/rule/detail/${item.policyId}`)}
              >
                {_$('未配置')}
              </span>
            ) : (
              <>
                <span>
                  {_$('联系组')}：{group?.length}
                </span>
                <br />
                <span>
                  {_$('联系人')}：{user?.length}
                </span>
              </>
            )}
          </span>
        )
      }
    },
    enable: {
      title: _$('状态'),
      width: '10%',
      template: function (item) {
        return (
          <Switch
            on={_$('启用')}
            off={_$('禁用')}
            width="62"
            value={item.enabled === 0}
            beforeChange={async (from, to) => {
              if (item.policyType === 1) {
                Message.warning('默认策略不能禁用')
              } else {
                const result = await EditEnablePolicy(to, {
                  PolicyIds: [item.policyId]
                })
                if (result?.code === 'Success') {
                  run()
                  Message.success(to ? _$('启用成功') : _$('禁用成功'))
                  return true
                }
                return false
              }
            }}
          />
        )
      }
    },
    action: {
      title: _$('操作'),
      width: '10%',
      template: function (item) {
        return (
          <div className="ksc-space">
            {/* 告警历史 */}
            <a
              onClick={() => {
                const policyId = encodeURIComponent(item.policyId)
                const policyName = encodeURIComponent(item.policyName)
                const src = `/monitor/alarmServer/history/detail/${policyId}/${policyName}`
                history.push(src)
              }}
            >
              {_$('告警历史')}
            </a>
            <Divider />
            {/* 修改 */}
            <a onClick={() => history.push(`/monitor/alarmServer/rule/detail/${item.policyId}`)}>
              {_$('修改')}
            </a>
            <Divider />
            <a
              className={item.policyType === 1 ? 'disabled-text' : ''}
              onClick={() => {
                let dialog: Dialog
                if (item.policyType === 1) {
                  Message.warning('默认策略不能删除')
                } else {
                  Dialog.confirm({
                    content: _$('确认删除当前策略？'),
                    ref: _dialog => (dialog = _dialog)
                  }).then(async () => {
                    dialog.showLoading()
                    const result = await DeleteAlarmPolicy({ PolicyIds: [item.policyId] })
                    if (result?.code === 'Success') {
                      Message.success(_$('策略删除成功'))
                      run()
                    } else {
                      Message.error(_$('策略删除失败'))
                    }
                    dialog.close()
                  })
                }
              }}
            >
              {_$('删除')}
            </a>
            <Divider />
            {item.policyType === 1 ? (
              <a
                onClick={async () => {
                  let dialog: Dialog
                  Dialog.confirm({
                    content: _$('确认取消当前默认策略？'),
                    ref: _dialog => (dialog = _dialog)
                  }).then(async () => {
                    dialog.showLoading()
                    await handleDefaultPolicy(item.policyId, item.policyType)
                    dialog.close()
                  })
                }}
              >
                取消默认策略
              </a>
            ) : (
              <a
                className={item.enabled === 1 || item.resourceBindType === 2 ? 'disabled-text' : ''}
                onClick={async () => {
                  let dialog: Dialog
                  if (item.enabled === 1) {
                    Message.warning('禁用状态下的策略不可设置为默认策略')
                  } else if (item.resourceBindType === 2) {
                    Message.warning('项目关联的策略不可设置为默认策略')
                  } else {
                    Dialog.confirm({
                      title: '是否确认设置当前默认策略？',
                      content:
                        '每类产品下只允许有一项默认策略。若当前产品下已有默认策略，开启后，之前的默认策略会变为“非默认”状态',
                      ref: _dialog => (dialog = _dialog),
                      okText: '确认设置',
                      ok: async () => {
                        dialog.showLoading()
                        await handleDefaultPolicy(item.policyId, item.policyType)
                        dialog.close()
                      }
                    })
                  }
                }}
              >
                设置默认策略
              </a>
            )}
          </div>
        )
      }
    }
  }
  const { run, result, total, pageState, loading }: any = useFormTable(
    API.GetPolicyList,
    {
      params: {
        ProductType: tableGroup.productName,
        PolicyName: policyName
      }
    },
    data => {
      return {
        tableData: data?.alarmPolicyList,
        totalCount: data?.totalCount
      }
    }
  )

  useEffect(() => {
    if (Array.isArray(result)) {
      const list = result
      if (Array.isArray(list)) {
        setPolicyList(list)
      }
    }
  }, [result])

  // 打开已选实例Dialog
  const openDialog = (polictId, projectName) => {
    setDialogPolicId(polictId)
    setDialogProjectName(projectName)
    setDialogVisible(true)
  }

  // 关闭已选实例Dialog
  const closeDialog = value => {
    // if (!value) {
    //   setDialogPolicId(null)
    //   setDialogProjectName('')
    // }
    setDialogVisible(value)
  }

  return (
    <div className={className}>
      <div className="header">
        <div>
          <Button
            type="primary"
            onClick={() => {
              history.push(`/monitor/alarmServer/rule/create`)
            }}
          >
            <Icon className="ion-plus-round"></Icon>
            {_$('新建告警策略')}
          </Button>
          <span className="text">
            <span>
              告警服务升级了，查看如何
              <a href="https://docs.ksyun.com/documents/31" target="_blank" rel="noreferrer">
                配置告警策略。
              </a>
            </span>
          </span>
        </div>
        <div>
          <MonitorSearch
            value={policyName}
            placeholder={_$('输入告警策略名称')}
            onChange={value => {
              setPolicyName(value)
              run({
                PageIndex: 1,
                ProductType: tableGroup.productName,
                PolicyName: value
              })
            }}
          />
        </div>
      </div>

      <TablePage
        tableProps={{
          scheme: schemeThreshold,
          data: policyList,
          checkType: 'none',
          resizable: true,
          group: tableGroup,
          onChangeGroup: value => {
            setTableGroup(value)
            run({
              PageIndex: 1,
              ProductType: value.productName,
              PolicyName: policyName
            })
          }
        }}
        run={run}
        loading={loading}
        pageProps={{ total, ...pageState }}
      />

      <TacticsInstancesDialog
        visible={dialogVisible}
        policyId={dialogPolicId}
        projectName={dialogProjectName}
        onClose={closeDialog}
      />
    </div>
  )
}

export default styled(TacticsList)`
  .header {
    min-height: 32px;
    line-height: 32px;
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    span.text {
      margin-left: 8px;
    }
  }
  .policyType_cell {
    display: flex;
    align-items: center;
    .k-input {
      width: 100%;
    }
    .k-editable {
      .c-ellipsis {
        white-space: normal;
      }
    }
    .metric-legend {
      max-width: 100%;
      margin: 0;
      word-break: break-all;
    }
  }
  .none-setting {
    cursor: pointer;
    color: ${variable.errorColor};
  }
  .k-table {
    tr {
      .k-tag {
        margin-left: 5px;
      }
      .text-overflow,
      p {
        margin: 0;
      }
    }
    .rule-list {
      margin-bottom: 0;
      li {
        max-width: 100%;
        height: 18px;
        span {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
`
